import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { BareLayout } from "../components/Layout";
import { Btn, Card, Field, Input, ErrorSummary, Notice } from "../components/kit";
import { api, parseError } from "../lib/api";
import { landingFor, NEXT_KEY, useAuth } from "../lib/auth";
import i18n from "../i18n";

function GoogleButton() {
  const { t } = useTranslation();
  const go = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/home";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <Btn variant="outline" className="w-full" onClick={go} testid="google-login-btn" type="button">
      <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden><path fill="#4285F4" d="M22.5 12.3c0-.8-.1-1.5-.2-2.3H12v4.3h5.9c-.3 1.4-1 2.5-2.2 3.3v2.7h3.6c2-1.9 3.2-4.7 3.2-8z"/><path fill="#34A853" d="M12 23c3 0 5.5-1 7.3-2.7l-3.6-2.7c-1 .7-2.2 1.1-3.7 1.1-2.9 0-5.3-1.9-6.2-4.5H2.1v2.8C3.9 20.6 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.8 14.2c-.2-.7-.4-1.4-.4-2.2s.1-1.5.4-2.2V7H2.1C1.4 8.5 1 10.2 1 12s.4 3.5 1.1 5l3.7-2.8z"/><path fill="#EA4335" d="M12 5.4c1.6 0 3.1.6 4.2 1.7l3.2-3.2C17.5 2.1 15 1 12 1 7.7 1 3.9 3.4 2.1 7l3.7 2.8C6.7 7.3 9.1 5.4 12 5.4z"/></svg>
      {t("auth.google")}
    </Btn>
  );
}

function useAfterAuth() {
  const navigate = useNavigate();
  const { signIn } = useAuth();
  return async (token) => {
    const me = await signIn(token);
    const next = sessionStorage.getItem(NEXT_KEY);
    sessionStorage.removeItem(NEXT_KEY);
    if (next?.startsWith("/invite/")) return navigate(next, { replace: true });
    navigate(me?.user?.role_choice && next ? next : landingFor(me), { replace: true });
  };
}

function Shell({ title, sub, children }) {
  return (
    <BareLayout>
      <div className="mx-auto grid max-w-5xl items-center gap-10 py-6 md:grid-cols-2 md:py-14">
        <div className="hidden md:block fade-up">
          <p className="font-display text-5xl font-extrabold leading-tight tracking-tight">{title}</p>
          <p className="mt-4 max-w-md text-lg text-muted-foreground">{sub}</p>
          <div className="mt-8 flex gap-3">
            <span className="h-16 w-16 rounded-[22px] bg-peach" />
            <span className="h-16 w-16 rounded-full bg-sage" />
            <span className="h-16 w-16 rounded-[22px] bg-lavender rotate-6" />
          </div>
        </div>
        <Card className="fade-up-2 w-full">{children}</Card>
      </div>
    </BareLayout>
  );
}

export function SignIn() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const after = useAfterAuth();
  const [f, setF] = useState({ email: "", password: "" });
  const [error, setError] = useState(params.get("google") ? { code: "google_failed" } : null);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const { data } = await api.post("/auth/login", f);
      await after(data.token);
    } catch (er) {
      setError(parseError(er));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Shell title={t("auth.hero_title")} sub={t("auth.hero_sub")}>
      <h1 className="text-2xl font-extrabold">{t("auth.sign_in")}</h1>
      <form onSubmit={submit} className="mt-5 space-y-4" noValidate>
        {error && <div role="alert" data-testid="login-error" className="rounded-xl border border-rose-line bg-rose p-3 text-sm font-semibold text-rose-ink">{t(`err.${error.code}`)}</div>}
        <Field label={t("field.email")} htmlFor="email"><Input id="email" type="email" autoComplete="email" data-testid="login-email-input" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></Field>
        <Field label={t("field.password")} htmlFor="password"><Input id="password" type="password" autoComplete="current-password" data-testid="login-password-input" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} /></Field>
        <Btn type="submit" className="w-full" disabled={busy} testid="login-submit-btn">{t("auth.sign_in")}</Btn>
      </form>
      <div className="my-4 flex items-center gap-3 text-xs text-muted-foreground"><span className="h-px flex-1 bg-border" />{t("auth.or")}<span className="h-px flex-1 bg-border" /></div>
      <GoogleButton />
      <p className="mt-5 text-center text-sm">{t("auth.no_account")} <Link to="/register" data-testid="go-register-link" className="font-semibold text-primary underline-offset-4 hover:underline">{t("auth.sign_up")}</Link></p>
    </Shell>
  );
}

export function SignUp() {
  const { t } = useTranslation();
  const after = useAfterAuth();
  const [f, setF] = useState({ first_name: "", surname: "", email: "", password: "" });
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const errs = error?.errors || [];
  const fe = (k) => { const x = errs.find((e) => e.field === k); return x && t(`err.${x.code}`); };
  const submit = async (e) => {
    e.preventDefault();
    const local = [];
    if (!f.email.includes("@")) local.push({ field: "email", code: "invalid_email" });
    if (f.password.length < 8) local.push({ field: "password", code: "password_too_short" });
    if (local.length) return setError({ code: "validation", errors: local });
    setBusy(true);
    setError(null);
    try {
      const { data } = await api.post("/auth/register", { ...f, interface_language: i18n.language?.startsWith("ru") ? "ru" : "en" });
      await after(data.token);
    } catch (er) {
      setError(parseError(er));
    } finally {
      setBusy(false);
    }
  };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <Shell title={t("auth.hero_title")} sub={t("auth.hero_sub")}>
      <h1 className="text-2xl font-extrabold">{t("auth.create_account")}</h1>
      <form onSubmit={submit} className="mt-5 space-y-4" noValidate>
        {error && error.code !== "validation" && <div role="alert" data-testid="register-error" className="rounded-xl border border-rose-line bg-rose p-3 text-sm font-semibold text-rose-ink">{t(`err.${error.code}`)}</div>}
        <ErrorSummary errors={errs} />
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t("field.first_name")} htmlFor="fn"><Input id="fn" data-testid="register-first-name-input" value={f.first_name} onChange={set("first_name")} /></Field>
          <Field label={t("field.surname")} htmlFor="sn"><Input id="sn" data-testid="register-surname-input" value={f.surname} onChange={set("surname")} /></Field>
        </div>
        <Field label={t("field.email")} htmlFor="email" error={fe("email")} required><Input id="email" type="email" data-testid="register-email-input" value={f.email} onChange={set("email")} /></Field>
        <Field label={t("field.password")} htmlFor="password" error={fe("password")} hint={t("auth.password_hint")} required><Input id="password" type="password" data-testid="register-password-input" value={f.password} onChange={set("password")} /></Field>
        <Btn type="submit" className="w-full" disabled={busy} testid="register-submit-btn">{t("auth.sign_up")}</Btn>
      </form>
      <div className="my-4 flex items-center gap-3 text-xs text-muted-foreground"><span className="h-px flex-1 bg-border" />{t("auth.or")}<span className="h-px flex-1 bg-border" /></div>
      <GoogleButton />
      <Notice testid="register-privacy-note">{t("auth.privacy_note")}</Notice>
      <p className="mt-5 text-center text-sm">{t("auth.have_account")} <Link to="/login" data-testid="go-login-link" className="font-semibold text-primary underline-offset-4 hover:underline">{t("auth.sign_in")}</Link></p>
    </Shell>
  );
}
